/**
 * Color utility functions for calculating contrast and determining optimal text colors
 */

/**
 * Convert hex color to RGB values
 * @param hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns RGB object with r, g, b values (0-255)
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
	// Remove # if present
	const cleanHex = hex.replace('#', '')
	
	// Handle 3-digit hex (e.g., "F00" -> "FF0000")
	const fullHex = cleanHex.length === 3 
		? cleanHex.split('').map(char => char + char).join('')
		: cleanHex

	const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex)
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16)
	} : null
}

/**
 * Calculate the relative luminance of a color
 * Based on WCAG 2.1 guidelines: https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)  
 * @param b - Blue value (0-255)
 * @returns Luminance value (0-1)
 */
export function getLuminance(r: number, g: number, b: number): number {
	// Convert RGB to sRGB
	const rsRGB = r / 255
	const gsRGB = g / 255
	const bsRGB = b / 255

	// Apply gamma correction
	const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
	const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
	const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4)

	// Calculate luminance using WCAG formula
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Calculate contrast ratio between two colors
 * @param color1 - First color luminance
 * @param color2 - Second color luminance
 * @returns Contrast ratio (1-21)
 */
export function getContrastRatio(luminance1: number, luminance2: number): number {
	const lighter = Math.max(luminance1, luminance2)
	const darker = Math.min(luminance1, luminance2)
	return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Determine if a color is considered "dark" based on its luminance
 * @param hex - Hex color string
 * @returns true if color is dark, false if light
 */
export function isDarkColor(hex: string): boolean {
	const rgb = hexToRgb(hex)
	if (!rgb) return false
	
	const luminance = getLuminance(rgb.r, rgb.g, rgb.b)
	// Threshold of 0.5 works well for most cases
	return luminance < 0.5
}

/**
 * Get optimal text color (black or white) for a given background color
 * @param backgroundHex - Background color in hex format
 * @returns "#000000" for dark backgrounds, "#ffffff" for light backgrounds
 */
export function getOptimalTextColor(backgroundHex: string): string {
	return isDarkColor(backgroundHex) ? "#ffffff" : "#000000"
}

/**
 * Get contrast ratio between background and text colors
 * @param backgroundHex - Background color in hex format
 * @param textHex - Text color in hex format  
 * @returns Contrast ratio (1-21)
 */
export function getTextBackgroundContrastRatio(backgroundHex: string, textHex: string): number {
	const bgRgb = hexToRgb(backgroundHex)
	const textRgb = hexToRgb(textHex)
	
	if (!bgRgb || !textRgb) return 1
	
	const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b)
	const textLuminance = getLuminance(textRgb.r, textRgb.g, textRgb.b)
	
	return getContrastRatio(bgLuminance, textLuminance)
}

/**
 * Check if a color combination meets WCAG AA accessibility standards (4.5:1 contrast ratio)
 * @param backgroundHex - Background color in hex format
 * @param textHex - Text color in hex format
 * @returns true if contrast meets AA standards
 */
export function meetsWCAGAA(backgroundHex: string, textHex: string): boolean {
	const contrastRatio = getTextBackgroundContrastRatio(backgroundHex, textHex)
	return contrastRatio >= 4.5
}

/**
 * Check if a color combination meets WCAG AAA accessibility standards (7:1 contrast ratio)
 * @param backgroundHex - Background color in hex format
 * @param textHex - Text color in hex format
 * @returns true if contrast meets AAA standards
 */
export function meetsWCAGAAA(backgroundHex: string, textHex: string): boolean {
	const contrastRatio = getTextBackgroundContrastRatio(backgroundHex, textHex)
	return contrastRatio >= 7
}

/**
 * Convert RGB to hex color
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Hex color string (e.g., "#FF0000")
 */
export function rgbToHex(r: number, g: number, b: number): string {
	const toHex = (value: number) => {
		const hex = Math.round(Math.max(0, Math.min(255, value))).toString(16)
		return hex.length === 1 ? '0' + hex : hex
	}
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

/**
 * Lighten a color by a given percentage
 * @param hex - Hex color string
 * @param percent - Percentage to lighten (0-100), default 15
 * @returns Lightened hex color string
 */
export function lightenColor(hex: string, percent: number = 15): string {
	const rgb = hexToRgb(hex)
	if (!rgb) return hex

	// Calculate the amount to lighten
	const amount = percent / 100

	// Lighten each RGB component
	const r = rgb.r + (255 - rgb.r) * amount
	const g = rgb.g + (255 - rgb.g) * amount
	const b = rgb.b + (255 - rgb.b) * amount

	return rgbToHex(r, g, b)
}

/**
 * Darken a color by a given percentage
 * @param hex - Hex color string
 * @param percent - Percentage to darken (0-100), default 15
 * @returns Darkened hex color string
 */
export function darkenColor(hex: string, percent: number = 15): string {
	const rgb = hexToRgb(hex)
	if (!rgb) return hex

	// Calculate the amount to darken
	const amount = 1 - (percent / 100)

	// Darken each RGB component
	const r = rgb.r * amount
	const g = rgb.g * amount
	const b = rgb.b * amount

	return rgbToHex(r, g, b)
}